import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
    IconButton, MenuItem, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchableSelect from '../common/SearchableSelect';
import { useEquipmentGroups } from './useEquipmentGroups';
import EditIcon from '@mui/icons-material/Edit';
import { AuthService } from '../../services/auth.service';
import { MaintenanceService } from '../../services/maintenance.service';
import { useToast } from '../../context/ToastContext';
import {
    EquipmentCategory, EquipmentGroupDTO, EquipmentTypeDTO, EquipmentTypeMeterDTO, FAILURE_CODE_KINDS,
    FailureCodeDTO, FailureCodeKind, MaintenanceSettings as Settings, METER_KINDS, MeterKind, mntLabel,
} from '../../interfaces/maintenance';

const DEFAULTS: Settings = {
    requestDuplicateWindowMinutes: 120, archiveClosedAfterDays: 0, pmGenerationHorizonDays: 7,
    requireFailureCodeOnClose: true, maintenanceStoreId: null,
};

const numField = { flex: '1 1 220px', maxWidth: 320, minWidth: 0 };

interface TypeDraft { id?: string; code: string; name: string; category: EquipmentCategory; active: boolean; notes: string; meters: EquipmentTypeMeterDTO[]; isSystem?: boolean }
interface GroupDraft { id?: string; code: string; name: string; sortOrder: string; active: boolean; notes: string; isSystem?: boolean }
interface CodeDraft { id?: string; kind: FailureCodeKind; code: string; name: string; equipmentCategory: EquipmentCategory | ''; active: boolean }

/** Maintenance settings, equipment types with meter templates, and failure codes. */
const MaintenanceSettings: React.FC = () => {
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution()!;
    const [settings, setSettings] = useState<Settings>(DEFAULTS);
    const [types, setTypes] = useState<EquipmentTypeDTO[]>([]);
    const [codes, setCodes] = useState<FailureCodeDTO[]>([]);
    const [typeDraft, setTypeDraft] = useState<TypeDraft | null>(null);
    const [codeDraft, setCodeDraft] = useState<CodeDraft | null>(null);
    const [groupDraft, setGroupDraft] = useState<GroupDraft | null>(null);
    const { groups, reload: reloadGroups, options: groupOptions, label: groupName } = useEquipmentGroups(institutionId);

    const load = useCallback(async () => {
        try {
            const [s, t, c] = await Promise.all([
                MaintenanceService.getSettings(institutionId),
                MaintenanceService.listTypes(institutionId),
                MaintenanceService.listFailureCodes(institutionId),
            ]);
            setSettings(s);
            setTypes(t);
            setCodes(c);
        } catch (e) {
            toast.showApiError(e, 'Failed to load maintenance settings');
        }
    }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const saveSettings = async () => {
        try {
            const { institutionId: _ignored, ...body } = settings;
            setSettings(await MaintenanceService.saveSettings(institutionId, body));
            toast.showSuccess('Settings saved');
        } catch (e) {
            toast.showApiError(e, 'Failed to save settings');
        }
    };

    const seed = async () => {
        try {
            const r = await MaintenanceService.seedDefaults(institutionId);
            toast.showSuccess(`Added ${r.equipmentTypesCreated} types, ${r.meterTemplatesCreated} meter templates, ${r.failureCodesCreated} failure codes`);
            load();
        } catch (e) {
            toast.showApiError(e, 'Failed to load defaults');
        }
    };

    const saveGroup = async () => {
        if (!groupDraft) return;
        if (!groupDraft.code.trim() || !groupDraft.name.trim()) { toast.showError('Code and name are required'); return; }
        const req = {
            institutionId, code: groupDraft.code.trim(), name: groupDraft.name.trim(),
            sortOrder: groupDraft.sortOrder.trim() === '' ? null : Number(groupDraft.sortOrder),
            active: groupDraft.active, notes: groupDraft.notes.trim() || null,
        };
        try {
            if (groupDraft.id) await MaintenanceService.updateGroup(groupDraft.id, req);
            else await MaintenanceService.createGroup(req);
            toast.showSuccess('Equipment group saved');
            setGroupDraft(null);
            reloadGroups();
        } catch (e) {
            toast.showApiError(e, 'Failed to save equipment group');
        }
    };

    const toggleGroup = async (g: EquipmentGroupDTO) => {
        try {
            await MaintenanceService.setGroupActive(g.id, !g.active);
            toast.showSuccess(g.active ? 'Group deactivated' : 'Group activated');
            reloadGroups();
        } catch (e) {
            toast.showApiError(e, 'Failed to change the group');
        }
    };

    const removeGroup = async (g: EquipmentGroupDTO) => {
        if (!window.confirm(`Delete group "${g.name}"?`)) return;
        try {
            await MaintenanceService.deleteGroup(g.id);
            toast.showSuccess('Equipment group deleted');
            reloadGroups();
        } catch (e) {
            toast.showApiError(e, 'Failed to delete the group');
        }
    };

    const saveType = async () => {
        if (!typeDraft) return;
        if (!typeDraft.code.trim() || !typeDraft.name.trim()) { toast.showError('Code and name are required'); return; }
        const req = {
            institutionId, code: typeDraft.code.trim(), name: typeDraft.name.trim(), category: typeDraft.category,
            active: typeDraft.active, notes: typeDraft.notes.trim() || null, meters: typeDraft.meters,
        };
        try {
            if (typeDraft.id) await MaintenanceService.updateType(typeDraft.id, req);
            else await MaintenanceService.createType(req);
            toast.showSuccess('Equipment type saved');
            setTypeDraft(null);
            load();
        } catch (e) {
            toast.showApiError(e, 'Failed to save equipment type');
        }
    };

    const removeType = async (t: EquipmentTypeDTO) => {
        if (!window.confirm(`Delete equipment type "${t.name}"?`)) return;
        try {
            await MaintenanceService.deleteType(t.id);
            toast.showSuccess('Equipment type deleted');
            load();
        } catch (e) {
            toast.showApiError(e, 'Failed to delete equipment type');
        }
    };

    const saveCode = async () => {
        if (!codeDraft) return;
        if (!codeDraft.code.trim() || !codeDraft.name.trim()) { toast.showError('Code and name are required'); return; }
        const req = {
            institutionId, kind: codeDraft.kind, code: codeDraft.code.trim(), name: codeDraft.name.trim(),
            equipmentCategory: codeDraft.equipmentCategory || null, active: codeDraft.active,
        };
        try {
            if (codeDraft.id) await MaintenanceService.updateFailureCode(codeDraft.id, req);
            else await MaintenanceService.createFailureCode(req);
            toast.showSuccess('Failure code saved');
            setCodeDraft(null);
            load();
        } catch (e) {
            toast.showApiError(e, 'Failed to save failure code');
        }
    };

    const removeCode = async (c: FailureCodeDTO) => {
        if (!window.confirm(`Delete failure code "${c.name}"?`)) return;
        try {
            await MaintenanceService.deleteFailureCode(c.id);
            toast.showSuccess('Failure code deleted');
            load();
        } catch (e) {
            toast.showApiError(e, 'Failed to delete failure code');
        }
    };

    const setNum = (k: keyof Settings) => (e: React.ChangeEvent<HTMLInputElement>) =>
        setSettings({ ...settings, [k]: e.target.value === '' ? 0 : Number(e.target.value) });

    const updateMeter = (i: number, patch: Partial<EquipmentTypeMeterDTO>) => {
        if (!typeDraft) return;
        setTypeDraft({ ...typeDraft, meters: typeDraft.meters.map((m, idx) => (idx === i ? { ...m, ...patch } : m)) });
    };

    return (
        <Box sx={{ p: { xs: 1, sm: 3 } }} data-help-page="maint.settings.page">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">Maintenance settings</Typography>
                <Button data-help="maint.settings.seed" variant="outlined" onClick={seed}>Load default types and failure codes</Button>
            </Box>

            <Paper sx={{ p: 2, mb: 3 }} data-help-section="maint.settings.general">
                <Typography variant="h6" sx={{ mb: 2 }}>General</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <TextField size="small" type="number" label="Duplicate request window (minutes)" data-help="maint.settings.field.dupWindow" sx={numField}
                        value={settings.requestDuplicateWindowMinutes} onChange={setNum('requestDuplicateWindowMinutes')} />
                    <TextField size="small" type="number" label="Archive closed and cancelled work orders after (days)" sx={numField}
                        data-help="maint.settings.archiveDays"
                        helperText="Moves them to the archive. 0 = never. Suggested: 365."
                        inputProps={{ min: 0, max: 3650 }}
                        value={settings.archiveClosedAfterDays} onChange={setNum('archiveClosedAfterDays')} />
                    <TextField size="small" type="number" label="PM generation horizon (days)" data-help="maint.settings.field.horizon" sx={numField}
                        value={settings.pmGenerationHorizonDays} onChange={setNum('pmGenerationHorizonDays')} />
                    <FormControlLabel label="Require failure code to close" data-help="maint.settings.field.requireCode" control={
                        <Checkbox checked={settings.requireFailureCodeOnClose}
                            onChange={e => setSettings({ ...settings, requireFailureCodeOnClose: e.target.checked })} />} />
                </Box>
                <Button data-help="maint.settings.save" variant="contained" sx={{ mt: 2 }} onClick={saveSettings}>Save settings</Button>
            </Paper>

            <Paper sx={{ p: 2, mb: 3 }} data-help-section="maint.settings.groups">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6">Equipment groups</Typography>
                    <Button data-help="maint.settings.action.addGroup" size="small" onClick={() => setGroupDraft({ code: '', name: '', sortOrder: '', active: true, notes: '' })}>
                        Add group
                    </Button>
                </Box>
                <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow><TableCell data-help="maint.settings.gcol.code">Code</TableCell><TableCell data-help="maint.settings.gcol.name">Name</TableCell><TableCell data-help="maint.settings.gcol.used">In use</TableCell><TableCell data-help="maint.settings.gcol.actions" /></TableRow>
                        </TableHead>
                        <TableBody>
                            {groups.length === 0 && <TableRow><TableCell colSpan={4} align="center">No groups</TableCell></TableRow>}
                            {groups.map(g => (
                                <TableRow key={g.id}>
                                    <TableCell>{g.code}</TableCell>
                                    <TableCell>{g.name}{!g.active && ' (inactive)'}{g.isSystem && <Chip size="small" label="Default" sx={{ ml: 1 }} />}</TableCell>
                                    <TableCell>{g.usedBy}</TableCell>
                                    <TableCell align="right">
                                        <IconButton data-help="maint.settings.action.editGroup" size="small" aria-label="Edit group" onClick={() => setGroupDraft({
                                            id: g.id, code: g.code, name: g.name, sortOrder: String(g.sortOrder), active: g.active,
                                            notes: g.notes ?? '', isSystem: g.isSystem,
                                        })}><EditIcon fontSize="small" /></IconButton>
                                        <Button data-help="maint.settings.action.toggleGroup" size="small" onClick={() => toggleGroup(g)}>{g.active ? 'Deactivate' : 'Activate'}</Button>
                                        {!g.isSystem && g.usedBy === 0 && <IconButton data-help="maint.settings.action.deleteGroup" size="small" aria-label="Delete group" onClick={() => removeGroup(g)}><DeleteIcon fontSize="small" /></IconButton>}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            </Paper>

            <Paper sx={{ p: 2, mb: 3 }} data-help-section="maint.settings.types">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6">Equipment types</Typography>
                    <Button data-help="maint.settings.action.addType" size="small" onClick={() => setTypeDraft({ code: '', name: '', category: groupOptions()[0]?.code ?? 'PRODUCTION_MACHINE', active: true, notes: '', meters: [] })}>
                        Add type
                    </Button>
                </Box>
                <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow><TableCell data-help="maint.settings.tcol.code">Code</TableCell><TableCell data-help="maint.settings.tcol.name">Name</TableCell><TableCell data-help="maint.settings.tcol.cat">Group</TableCell><TableCell data-help="maint.settings.tcol.meters">Meters</TableCell><TableCell data-help="maint.settings.tcol.actions" /></TableRow>
                        </TableHead>
                        <TableBody>
                            {types.length === 0 && <TableRow><TableCell colSpan={5} align="center">No equipment types</TableCell></TableRow>}
                            {types.map(t => (
                                <TableRow key={t.id}>
                                    <TableCell>{t.code}</TableCell>
                                    <TableCell>{t.name}{!t.active && ' (inactive)'}{t.isSystem && <Chip size="small" label="Default" sx={{ ml: 1 }} />}</TableCell>
                                    <TableCell>{groupName(t.category)}</TableCell>
                                    <TableCell>{t.meters.length}</TableCell>
                                    <TableCell align="right">
                                        <IconButton data-help="maint.settings.action.editType" size="small" aria-label="Edit type" onClick={() => setTypeDraft({
                                            id: t.id, code: t.code, name: t.name, category: t.category, active: t.active,
                                            notes: t.notes ?? '', meters: t.meters, isSystem: t.isSystem,
                                        })}><EditIcon fontSize="small" /></IconButton>
                                        {!t.isSystem && <IconButton data-help="maint.settings.action.deleteType" size="small" aria-label="Delete type" onClick={() => removeType(t)}><DeleteIcon fontSize="small" /></IconButton>}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            </Paper>

            <Paper sx={{ p: 2 }} data-help-section="maint.settings.codes">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6">Failure codes</Typography>
                    <Button data-help="maint.settings.action.addCode" size="small" onClick={() => setCodeDraft({ kind: 'SYMPTOM', code: '', name: '', equipmentCategory: '', active: true })}>
                        Add failure code
                    </Button>
                </Box>
                <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow><TableCell data-help="maint.settings.ccol.kind">Kind</TableCell><TableCell data-help="maint.settings.ccol.code">Code</TableCell><TableCell data-help="maint.settings.ccol.name">Name</TableCell><TableCell data-help="maint.settings.ccol.cat">Group</TableCell><TableCell data-help="maint.settings.ccol.actions" /></TableRow>
                        </TableHead>
                        <TableBody>
                            {codes.length === 0 && <TableRow><TableCell colSpan={5} align="center">No failure codes</TableCell></TableRow>}
                            {codes.map(c => (
                                <TableRow key={c.id}>
                                    <TableCell>{mntLabel(c.kind)}</TableCell>
                                    <TableCell>{c.code}</TableCell>
                                    <TableCell>{c.name}{!c.active && ' (inactive)'}</TableCell>
                                    <TableCell>{c.equipmentCategory ? groupName(c.equipmentCategory) : 'All'}</TableCell>
                                    <TableCell align="right">
                                        <IconButton data-help="maint.settings.action.editCode" size="small" aria-label="Edit failure code" onClick={() => setCodeDraft({
                                            id: c.id, kind: c.kind, code: c.code, name: c.name, equipmentCategory: c.equipmentCategory ?? '', active: c.active,
                                        })}><EditIcon fontSize="small" /></IconButton>
                                        <IconButton data-help="maint.settings.action.deleteCode" size="small" aria-label="Delete failure code" onClick={() => removeCode(c)}><DeleteIcon fontSize="small" /></IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            </Paper>

            <Dialog open={!!groupDraft} onClose={() => setGroupDraft(null)} fullWidth maxWidth="xs" PaperProps={{ 'data-help-page': 'maint.groupForm.page' } as any}>
                <DialogTitle>{groupDraft?.id ? 'Edit equipment group' : 'Add equipment group'}</DialogTitle>
                {groupDraft && (
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                        <TextField size="small" required label="Code" value={groupDraft.code} data-help="maint.groupForm.field.code"
                            disabled={!!groupDraft.id} inputProps={{ maxLength: 30 }}
                            helperText={groupDraft.id ? 'The code cannot change.' : 'Letters, digits and underscore, for example WATER_MGMT.'}
                            onChange={e => setGroupDraft({ ...groupDraft, code: e.target.value })} />
                        <TextField size="small" required label="Name" value={groupDraft.name} data-help="maint.groupForm.field.name"
                            onChange={e => setGroupDraft({ ...groupDraft, name: e.target.value })} />
                        <TextField size="small" type="number" label="Sort order" value={groupDraft.sortOrder} data-help="maint.groupForm.field.sort"
                            onChange={e => setGroupDraft({ ...groupDraft, sortOrder: e.target.value })} />
                        <TextField size="small" label="Notes" multiline minRows={2} value={groupDraft.notes} data-help="maint.groupForm.field.notes"
                            onChange={e => setGroupDraft({ ...groupDraft, notes: e.target.value })} />
                        <FormControlLabel label="Active" data-help="maint.groupForm.field.active" control={<Checkbox checked={groupDraft.active} onChange={e => setGroupDraft({ ...groupDraft, active: e.target.checked })} />} />
                    </DialogContent>
                )}
                <DialogActions>
                    <Button onClick={() => setGroupDraft(null)} data-help="maint.groupForm.cancel">Cancel</Button>
                    <Button data-help="maint.settings.action.saveGroup" variant="contained" onClick={saveGroup}>Save</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!typeDraft} onClose={() => setTypeDraft(null)} fullWidth maxWidth="md" PaperProps={{ 'data-help-page': 'maint.typeForm.page' } as any}>
                <DialogTitle>{typeDraft?.id ? 'Edit equipment type' : 'Add equipment type'}</DialogTitle>
                {typeDraft && (
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, pt: 1 }}>
                            <TextField size="small" required label="Code" sx={numField} value={typeDraft.code} data-help="maint.typeForm.field.code"
                                onChange={e => setTypeDraft({ ...typeDraft, code: e.target.value })} />
                            <TextField size="small" required label="Name" sx={numField} value={typeDraft.name} data-help="maint.typeForm.field.name"
                                onChange={e => setTypeDraft({ ...typeDraft, name: e.target.value })} />
                            <Box sx={numField}>
                                <SearchableSelect
                                    options={groupOptions(typeDraft.category)} value={typeDraft.category} size="small" label="Group" required clearable={false}
                                    data-help="maint.typeForm.field.category" getOptionId={g => g.code} getOptionLabel={g => g.name}
                                    onChange={code => setTypeDraft({ ...typeDraft, category: code as EquipmentCategory })}
                                />
                            </Box>
                            <FormControlLabel label="Active" data-help="maint.typeForm.field.active" control={
                                <Checkbox checked={typeDraft.active} onChange={e => setTypeDraft({ ...typeDraft, active: e.target.checked })} />} />
                        </Box>
                        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Meter templates (copied to new equipment of this type)</Typography>
                        {typeDraft.meters.map((m, i) => (
                            <Box key={i} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1, alignItems: 'center' }}>
                                <TextField size="small" label="Code" sx={{ flex: '1 1 110px', maxWidth: 160 }} data-help="maint.typeForm.meter.code" value={m.code} onChange={e => updateMeter(i, { code: e.target.value })} />
                                <TextField size="small" label="Name" sx={{ flex: '1 1 150px', maxWidth: 240 }} data-help="maint.typeForm.meter.name" value={m.name} onChange={e => updateMeter(i, { name: e.target.value })} />
                                <TextField select size="small" label="Kind" sx={{ flex: '1 1 130px', maxWidth: 180 }} data-help="maint.typeForm.meter.kind" value={m.kind} onChange={e => updateMeter(i, { kind: e.target.value as MeterKind })}>
                                    {METER_KINDS.map(k => <MenuItem key={k} value={k}>{mntLabel(k)}</MenuItem>)}
                                </TextField>
                                <TextField size="small" label="Unit" sx={{ flex: '1 1 80px', maxWidth: 120 }} data-help="maint.typeForm.meter.unit" value={m.unit ?? ''} onChange={e => updateMeter(i, { unit: e.target.value })} />
                                <FormControlLabel label="PM driver" data-help="maint.typeForm.meter.pmDriver" control={<Checkbox checked={!!m.isPmDriver} onChange={e => updateMeter(i, { isPmDriver: e.target.checked })} />} />
                                <IconButton data-help="maint.settings.action.removeMeter" size="small" aria-label="Remove meter template"
                                    onClick={() => setTypeDraft({ ...typeDraft, meters: typeDraft.meters.filter((_, idx) => idx !== i) })}><DeleteIcon fontSize="small" /></IconButton>
                            </Box>
                        ))}
                        <Button data-help="maint.settings.action.addMeter" size="small" onClick={() => setTypeDraft({ ...typeDraft, meters: [...typeDraft.meters, { code: '', name: '', kind: 'GAUGE', unit: '', isPmDriver: false }] })}>
                            Add meter template
                        </Button>
                    </DialogContent>
                )}
                <DialogActions>
                    <Button onClick={() => setTypeDraft(null)} data-help="maint.typeForm.cancel">Cancel</Button>
                    <Button data-help="maint.settings.action.saveType" variant="contained" onClick={saveType}>Save</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!codeDraft} onClose={() => setCodeDraft(null)} fullWidth maxWidth="xs" PaperProps={{ 'data-help-page': 'maint.failureCode.page' } as any}>
                <DialogTitle>{codeDraft?.id ? 'Edit failure code' : 'Add failure code'}</DialogTitle>
                {codeDraft && (
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                        <TextField select size="small" label="Kind" value={codeDraft.kind} data-help="maint.failureCode.field.kind"
                            onChange={e => setCodeDraft({ ...codeDraft, kind: e.target.value as FailureCodeKind })}>
                            {FAILURE_CODE_KINDS.map(k => <MenuItem key={k} value={k}>{mntLabel(k)}</MenuItem>)}
                        </TextField>
                        <TextField size="small" required label="Code" value={codeDraft.code} data-help="maint.failureCode.field.code" onChange={e => setCodeDraft({ ...codeDraft, code: e.target.value })} />
                        <TextField size="small" required label="Name" value={codeDraft.name} data-help="maint.failureCode.field.name" onChange={e => setCodeDraft({ ...codeDraft, name: e.target.value })} />
                        <SearchableSelect
                            options={groupOptions(codeDraft.equipmentCategory)} value={codeDraft.equipmentCategory} size="small"
                            label="Equipment group (blank = all)" data-help="maint.failureCode.field.category"
                            getOptionId={g => g.code} getOptionLabel={g => g.name}
                            onChange={code => setCodeDraft({ ...codeDraft, equipmentCategory: code as EquipmentCategory | '' })}
                        />
                        <FormControlLabel label="Active" data-help="maint.failureCode.field.active" control={<Checkbox checked={codeDraft.active} onChange={e => setCodeDraft({ ...codeDraft, active: e.target.checked })} />} />
                    </DialogContent>
                )}
                <DialogActions>
                    <Button onClick={() => setCodeDraft(null)} data-help="maint.failureCode.cancel">Cancel</Button>
                    <Button data-help="maint.settings.action.saveCode" variant="contained" onClick={saveCode}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MaintenanceSettings;
